/* eslint-disable react-hooks/exhaustive-deps */
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "lodash";
import React from "react";
import { api } from "../../api/api";
import { CustomFormLabel, CustomTextField } from "../formsComponents";
import { IUser, IUsersAutoSearch } from "./AutoCompleteSearches.types";
import { useBillingCompanyContext } from "../../providers";
import { AddQuickUserForm } from "../../views/Profile/UsersList/AddQuickUserForm";

const INITIAL_STATE: IUser = {
  first_name: "",
  personal_email: "",
  user_uuid: "",
  last_name: "",
  job_title: null,
  manager_uuid: null,
  user_type: null,
  assigned_phone_number: null,
  shared_email: null,
  mobile: null,
  home_phone: null,
  linkedin_profile: null,
  hire_date: null,
  last_day_at_work: null,
  branch: null,
  department: null,
  fax: null,
  date_of_birth: null,
  mother_maiden_name: null,
  photo: null,
  signature: null,
  street_address: null,
  unit_or_suite: null,
  city: null,
  province_or_state: null,
  postal_code: null,
  country: null,
  languages_known: null,
  documents: null,
  status: "ACTIVE",
};

export const UsersAutoSearch: React.FC<IUsersAutoSearch> = (props) => {
  const {
    label,
    value,
    onSelect,
    disabled,
    error,
    roleGroup,
    roleValue,
    hideAddButton,
    placeholder = "",
  } = props;

  const [allUsers, setAllUsers] = React.useState<IUser[]>([]);
  const [filteredOptions, setFilteredOptions] = React.useState<IUser[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [search, setSearchText] = React.useState<string>("");
  const [openCreateUser, setOpenCreateUser] = React.useState<
    "QUICK" | "FULL" | null
  >(null);
  const { comapny } = useBillingCompanyContext();
  const userRoleGroup = roleGroup ?? null;

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      let query = `status=ACTIVE&pageNo=1&itemPerPage=1000`; // you can paginate if too large

      if (comapny.customer_uuid) {
        query += `&billing_company_uuid=${comapny.customer_uuid}`;
      }
      if (roleGroup) {
        query += `&role_group=${roleGroup}`;
      }
      if (roleValue?.length) {
        query += `&role_value=${roleValue.join(",")}`;
      }

      const res = await api.get<{ data: IUser[] }>(`/user/get-user?${query}`);
      setAllUsers(res.data.data);
      setFilteredOptions(res.data.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAllUsers();
  }, []);

  React.useEffect(() => {
    if (search.length === 0) {
      setFilteredOptions(allUsers);
    } else {
      const filtered = allUsers.filter((user) =>
        `${user.first_name} ${user.last_name}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      );
      setFilteredOptions(filtered);
    }
  }, [search, allUsers]);

  React.useEffect(() => {
    if (value?.uuid && allUsers.length > 0) {
      const exists = allUsers.find((u) => u.user_uuid === value.uuid);
      if (!exists) {
        const fullName = value.name?.split(" ") || [];
        setAllUsers((prev) => [
          ...prev,
          {
            ...INITIAL_STATE,
            user_uuid: value.uuid,
            first_name: fullName[0] || "",
            last_name: fullName[1] || "",
          },
        ]);
      }
    }
  }, [value, allUsers]);

  const getOptionLabel = (option: IUser | string) => {
    if (typeof option === "string") return option;
    return `${option.first_name || ""} ${option.last_name || ""}`.trim();
  };

  const selectedValue = React.useMemo(() => {
    return allUsers.find((option) => option.user_uuid === value?.uuid) || null;
  }, [allUsers, value]);

  return (
    <>
      {label && (
        <Box display="flex" justifyContent={"flex-start"} alignItems="center">
          <CustomFormLabel display={"block"}>{label}</CustomFormLabel>
          {!hideAddButton &&
            search.length > 0 &&
            filteredOptions.length === 0 &&
            !loading && (
              <Box
                sx={{
                  paddingLeft: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  marginTop: 1,
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 1000,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenCreateUser("QUICK");
                }}
              >
                <AddCircleIcon fontSize="small" color="primary" />
                <Typography
                  variant="h6"
                  color="primary"
                  marginLeft={0.5}
                  marginTop={0.3}
                  fontWeight={500}
                >
                  Add User
                </Typography>
              </Box>
            )}
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Autocomplete
          id="user-autosearch"
          fullWidth
          disabled={disabled || loading}
          options={filteredOptions}
          value={selectedValue}
          loading={loading}
          filterOptions={(x) => x}
          autoComplete
          includeInputInList
          onFocus={() => {
            if (!allUsers.length) fetchAllUsers();
          }}
          onInputChange={(event, newInputValue, reason) => {
            setSearchText(newInputValue);
            if (reason === "clear") {
              setFilteredOptions(allUsers);
            }
          }}
          onChange={(event: React.ChangeEvent<{}>, newValue: IUser | null) => {
            if (newValue) {
              onSelect(newValue);
            } else {
              onSelect(INITIAL_STATE);
            }
          }}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) =>
            option.user_uuid === value?.user_uuid
          }
          noOptionsText={
            search.length > 0
              ? `No user found for "${search}"`
              : "No users available"
          }
          sx={{
            ".MuiOutlinedInput-root": {
              paddingTop: "2px",
              paddingBottom: "2px",
              fontSize: "0.8rem",
              color: "rgb(38, 38, 38)",
              width: "100%",
              backgroundColor: disabled ? "#f8f9fb" : "inherit",
            },
          }}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              fullWidth
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              disabled={disabled}
              error={!!error}
              helperText={error}
            />
          )}
        />
      </Stack>

      {openCreateUser === "QUICK" && (
        <AddQuickUserForm open={true} onClose={() => setOpenCreateUser(null)} />
      )}
    </>
  );
};
